import { Core, Decorative } from "@faire/design-tokens";
import { MobileSlider } from "@faire/retailer-visitor-shared/components/MobileSlider/index";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React, { type JSX } from "react";
import styled from "styled-components";

const MOBILE_REGION_WIDTH = 156;

export const GeographicMapMobile: React.FC = () => {
  const { isEurope } = useNationality();
  return (
    <>
      <SectionHeadingWrapper>
        <Typography variant="pageHeaderSerifRegular">
          <StrictLocalMsg
            defaultMessage="Get more sales at home and abroad"
            description={{ text: "Header for Faire geographic map section" }}
          />
        </Typography>
      </SectionHeadingWrapper>
      <Spacer size="2x" />
      <MobileSlider
        numberOfSlides={3}
        arrows={false}
        centerMode
        centerPadding="24px"
        slidesToShow={2}
        slidesToScroll={1}
      >
        {isEurope ? <MapNorthAmerica /> : <MapEurope />}
        <MapAustralia />
        {isEurope ? <MapEurope /> : <MapNorthAmerica />}
      </MobileSlider>
    </>
  );
};

const MapEurope: React.FC = () => {
  const isSSR = useIsSSR();
  const MOBILE_EU = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/135f63113b909cd894d4568a59a7cf071bff05837115b7dcfe678fbcad656134.png",
    },
    isSSR,
    { width: MOBILE_REGION_WIDTH }
  );

  return (
    <MapCard
      mapUrl={MOBILE_EU}
      name={
        <StrictLocalMsg
          defaultMessage="Europe"
          description={{
            text: "European retailers on the Faire platform",
          }}
        />
      }
    />
  );
};

const MapNorthAmerica: React.FC = () => {
  const isSSR = useIsSSR();
  const MOBILE_NA = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/c1e3006584ec38a7ee9f399974db75677f10158be0849e793cd18e929977f6be.png",
    },
    isSSR,
    { width: MOBILE_REGION_WIDTH }
  );

  return (
    <MapCard
      mapUrl={MOBILE_NA}
      name={
        <StrictLocalMsg
          defaultMessage="North America"
          description={{
            text: "Number of North American retailers on the Faire platform",
          }}
        />
      }
    />
  );
};

const MapAustralia: React.FC = () => {
  const isSSR = useIsSSR();
  const MOBILE_AUS = optimizeImageUrl(
    {
      url: "https://cdn.faire.com/fastly/1be730e0c48b75153ae88186884a550eb6ec2e7b9b220d8bc18b5aeefc741268.png",
    },
    isSSR,
    { width: MOBILE_REGION_WIDTH }
  );

  return (
    <MapCard
      mapUrl={MOBILE_AUS}
      name={
        <StrictLocalMsg
          defaultMessage="Australia"
          description={{ text: "Faire recently launched in Australia" }}
        />
      }
    />
  );
};

const MapCard: React.FC<{
  mapUrl?: string;
  name: JSX.Element;
}> = ({ mapUrl, name }) => {
  return (
    <GeographicCard>
      <img src={mapUrl} style={{ width: "100%" }} />
      <DetailsTag flex={1}>
        <Typography
          variant={{
            mobileAndAbove: "displaySSerifRegular",
            tabletAndAbove: "pageHeaderSerifRegular",
          }}
        >
          {name}
        </Typography>
      </DetailsTag>
    </GeographicCard>
  );
};

const SectionHeadingWrapper = styled.div`
  padding-left: ${getSpacing("3x")};
`;

const GeographicCard = styled(Column)`
  border: 1px solid ${Core.border.muted};
  background-color: ${Decorative.tint.neutral};
  margin: ${getSpacing(0, "1x")};
`;

const DETAILS_HEIGHT = "128px";
/**
 * @trackcomponent
 * @supersededBy "@faire/web--source/slate/Tag/Tag#Tag"
 */
const DetailsTag = styled(Column)`
  min-height: ${DETAILS_HEIGHT};
  max-height: ${DETAILS_HEIGHT};
  background-color: ${Core.surface.inverse};
  padding: 16px;
  border: 1px solid ${Core.border.muted};
  box-sizing: border-box;
`;
